import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

interface FieldProps {
    id: string
    label: string
    children: ReactNode
    hint?: string
    error?: string
    optional?: boolean
    className?: string
}

export const inputClass = cn(
    'block w-full rounded-none border-0 border-b border-ivory/20 bg-transparent px-0 py-3 text-[1.05rem] text-ivory',
    'placeholder:text-ivory/30 transition-colors duration-500 ease-film',
    'hover:border-ivory/40 focus:border-brass focus:outline-none focus:ring-0',
    'aria-[invalid=true]:border-destructive/80'
)

export const describedBy = (id: string, hint?: string, error?: string): string | undefined => {
    const ids = [error ? `${id}-error` : null, hint ? `${id}-hint` : null].filter(Boolean)

    return ids.length ? ids.join(' ') : undefined
}

const Field = ({ id, label, children, hint, error, optional = false, className }: FieldProps): ReactNode => {
    return (
        <div className={className}>
            <label htmlFor={id} className='caps flex items-baseline justify-between gap-4 text-ivory/60'>
                <span>{label}</span>
                {optional && <span className='text-[0.625rem] text-ivory/35'>Optional</span>}
            </label>

            <div className='mt-2'>
                {children}
            </div>

            {hint && (
                <p id={`${id}-hint`} className='mt-2 text-sm leading-relaxed text-ivory/45'>
                    {hint}
                </p>
            )}

            {error && (
                <p id={`${id}-error`} className='mt-2 text-sm leading-relaxed text-destructive'>
                    {error}
                </p>
            )}
        </div>
    )
}

export default Field
